import { Injectable } from '@angular/core';
import { Apollo } from 'apollo-angular';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { ulid } from 'ulid';

import { ICreditWalletManagementRepository } from '@modules/credit-wallet/Domain/Interfaces/credit-wallet-management.repository';
import { CreateCreditWalletInput, PurchaseCreditsInput } from '@modules/credit-wallet/Domain/Interfaces/credit-wallet-management.inputs';
import { CreditWallet } from '@modules/credit-wallet/Domain/Entities/credit-wallet.entity';
import { GET_CREDIT_WALLET } from '@modules/credit-wallet/Infrastructure/GraphQL/queries';
import { CREATE_CREDIT_WALLET, PURCHASE_CREDITS } from '@modules/credit-wallet/Infrastructure/GraphQL/mutations';
import { adaptCreditWallet, RawCreditWallet } from '@modules/credit-wallet/Infrastructure/Adapters/credit-wallet.adapter';

@Injectable({ providedIn: 'root' })
export class CreditWalletManagementApolloRepository extends ICreditWalletManagementRepository {
  constructor(private readonly apollo: Apollo) {
    super();
  }

  /** Returns the wallet or null if the tenant has no wallet yet. */
  getByTenantId(tenantId: string): Observable<CreditWallet | null> {
    return this.apollo
      .query<{ creditWallet: RawCreditWallet | null }>({
        query: GET_CREDIT_WALLET,
        variables: { tenantId },
        fetchPolicy: 'no-cache',
      })
      .pipe(
        map((res) => (res.data.creditWallet ? adaptCreditWallet(res.data.creditWallet) : null)),
        catchError(() => of(null)),
      );
  }

  create(input: CreateCreditWalletInput): Observable<CreditWallet> {
    const payload: Record<string, unknown> = { id: ulid(), tenantId: input.tenantId, plan: input.plan };
    if (input.customPlanCredits !== undefined) {
      payload['customPlanCredits'] = input.customPlanCredits;
    }
    return this.apollo
      .mutate<{ createCreditWallet: RawCreditWallet }>({
        mutation: CREATE_CREDIT_WALLET,
        variables: { input: payload },
      })
      .pipe(map((res) => adaptCreditWallet(res.data!.createCreditWallet)));
  }

  purchaseCredits(input: PurchaseCreditsInput): Observable<CreditWallet> {
    return this.apollo
      .mutate<{ purchaseCredits: RawCreditWallet }>({
        mutation: PURCHASE_CREDITS,
        variables: {
          input: {
            id: ulid(),
            tenantId: input.tenantId,
            credits: input.credits,
            reference: input.reference,
          },
        },
      })
      .pipe(map((res) => adaptCreditWallet(res.data!.purchaseCredits)));
  }
}
